import { OrderItemStatus } from '@/lib/shared-types';

/**
 * The waiter station's pass, as arithmetic.
 *
 * Ported from the waiter app's `src/lib/pass.ts`, which is the specification for
 * this half of the product, and kept out of the screen for the same reason it is
 * kept out of `PassScreen.tsx`: the failure this exists to prevent — the freshest
 * plate sorting to the top — is invisible in a screenshot and provable in a test.
 *
 * What is deliberately *not* ported is the belief map. The handheld needs one
 * because its writes go through a durable offline outbox and may be on disk
 * rather than on the wire; this dashboard fires the same two routes through
 * axios and invalidates the cache, exactly as `Kitchen.tsx` does, so there is
 * never a gap to bridge and a second source of truth would only be something for
 * a refetch to disagree with.
 *
 * The input types are structural rather than imported from `operationsApi`: the
 * only fields this file reads are the ones below, and a module that names its
 * own inputs can be tested without a store.
 */

/**
 * The item states that belong to the waiter rather than to the kitchen.
 *
 * `AWAITING_PICKUP` is here for completeness rather than because the API
 * produces it — `KitchenService.pickUpItem` validates
 * READY → AWAITING_PICKUP → PICKED_UP and then writes a single row straight to
 * PICKED_UP — but a row that somehow is in it still needs collecting, and
 * dropping it would strand exactly the plate nobody can explain.
 */
export const PASS_STATUSES: readonly string[] = [
  OrderItemStatus.READY,
  OrderItemStatus.AWAITING_PICKUP,
  OrderItemStatus.PICKED_UP,
];

/**
 * The two writes this screen makes, and why it is two and not one.
 *
 * The item machine allows READY → SERVED directly and `KitchenService.serveItem`
 * takes that edge, writing `servedAt` and leaving `pickedUpAt` NULL for ever
 * because nothing backfills it. The pickup-delay measure is
 * `AVG(picked_up_at - ready_at)` and SQL `AVG` skips NULL rows in silence, so a
 * one-tap screen would not break that report — it would quietly compute it over
 * only the plates that happened to go the long way round. `pickup` is therefore
 * always offered before `serve`, and `serve` only to a line already collected.
 */
export type PassAction = 'pickup' | 'serve';

export interface PassItemInput {
  id: string;
  name: string;
  quantity: string;
  status: string;
  notes: string | null;
  readyAt: string | null;
}

export interface PassOrderInput {
  id: string;
  number: string;
  fulfillmentType: string;
  customerName: string | null;
  createdAt: string;
  session?: { id: string; tableId: string | null } | null;
  items: PassItemInput[];
}

/** What a table is called, resolved once out of `GET /tables`. */
export interface TableLabel {
  number: string;
  areaName: string | null;
}

/** One line of food waiting on a waiter. */
export interface PassItem {
  /** The `OrderItem` id — what `POST /kitchen/items/:id/{pickup,serve}` takes. */
  id: string;
  orderId: string;
  orderNumber: string;
  name: string;
  quantity: string;
  status: string;
  notes: string | null;
  /** When this line landed on the pass, epoch ms, or null when nothing could say. */
  since: number | null;
  /** The transition this line is owed next. Never a state it is already in. */
  next: PassAction;
}

/** One trip: everything on the pass for a single table, or one takeaway bag. */
export interface PassTrip {
  /**
   * Stable identity for the group: the table's id where there is one, the
   * order's id where there is not. A takeaway order and a dine-in order whose
   * session lost its table must not collapse into one "no table" pile that
   * nobody can act on row by row.
   */
  key: string;
  tableId: string | null;
  /** The venue's own table number. Isolated where it is drawn, never here. */
  tableNumber: string | null;
  areaName: string | null;
  /** True when no order in this trip is dine-in — a counter bag, not a table. */
  takeaway: boolean;
  /** Whose bag it is, when the trip has no table and the order named someone. */
  customerName: string | null;
  /** Every order contributing to this trip, oldest first. */
  orderNumbers: string[];
  items: PassItem[];
  /** How long the *oldest* line here has waited, in ms. Null when unknown. */
  waitingMs: number | null;
  /** Lines still to collect from the pass, and lines still to put down. */
  toCollect: number;
  toServe: number;
}

/** A wait, in the units a waiter reads it in. */
export interface Waited {
  hours: number;
  minutes: number;
  totalMinutes: number;
}

/**
 * `T-2` before `T-10`, and Arabic-Indic digits compared as digits.
 *
 * `localeCompare` with `numeric` rather than a hand-rolled split: table numbers
 * are the venue's own strings and a venue that numbers its tables `طاولة ٣`
 * sorts correctly here and nowhere else.
 */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const compareNatural = (a: string, b: string): number => collator.compare(a, b);

/**
 * `tableId → what to call it`.
 *
 * An order carries `session.tableId` and nothing more, and a UUID is not an
 * instruction. Built from the same `GET /tables` snapshot the floor strip reads,
 * so the station makes one request for both.
 */
export function tableIndex(
  tables: readonly { id: string; number: string; area?: { name: string } | null }[],
): Record<string, TableLabel> {
  const index: Record<string, TableLabel> = {};
  for (const table of tables) {
    index[table.id] = { number: table.number, areaName: table.area?.name ?? null };
  }
  return index;
}

/**
 * When a line landed on the pass.
 *
 * `readyAt` is the truth and is what the pickup-delay report is measured
 * against. The fallback exists because a row whose stamp is missing must still
 * sort and must still show a number: a plate with a blank timer reads as "not
 * urgent", which is the one thing a plate under a lamp is never allowed to read
 * as. `createdAt` is older than the true ready time, so the fallback can only
 * ever *over*-state the wait, never under-state it.
 */
function landedAt(item: PassItemInput, order: PassOrderInput): number | null {
  for (const candidate of [item.readyAt, order.createdAt]) {
    if (!candidate) continue;
    const parsed = Date.parse(candidate);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

const nextAction = (status: string): PassAction =>
  status === OrderItemStatus.PICKED_UP ? 'serve' : 'pickup';

/** Oldest first; a row with no timestamp sinks below every row that has one. */
function byAge(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/**
 * Longest wait first — the same ordering as {@link byAge} expressed over a
 * duration, which runs the other way. A group whose age could not be established
 * still sinks rather than sorting as "0 minutes", which would float it to the top
 * of a screen whose whole ordering is urgency.
 */
function byWait(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

/**
 * The whole pass, from the two reads that produce it.
 *
 * Grouped by **table**, because a waiter walks to a table and not to an item:
 * three plates and a coffee for T-05 are one trip, and four rows that have to be
 * spotted as belonging together are four trips waiting to happen. Two separate
 * orders on the same table merge into that one trip for the same reason.
 *
 * Ordered by how long the oldest line in each group has been waiting, longest
 * first. That ordering is the point of the screen rather than a nicety: food
 * under a lamp is what actually goes wrong, and a list sorted by table number
 * hides the plate that has been sitting eleven minutes behind two that were
 * called thirty seconds ago.
 */
export function buildPass(
  orders: readonly PassOrderInput[],
  tables: Record<string, TableLabel>,
  now: number,
): PassTrip[] {
  const groups = new Map<string, PassTrip>();

  for (const order of orders) {
    const tableId = order.session?.tableId ?? null;
    const key = tableId ?? order.id;
    const label = tableId ? tables[tableId] : undefined;

    for (const item of order.items ?? []) {
      if (!PASS_STATUSES.includes(item.status)) continue;

      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          tableId,
          tableNumber: label?.number ?? null,
          areaName: label?.areaName ?? null,
          takeaway: true,
          customerName: null,
          orderNumbers: [],
          items: [],
          waitingMs: null,
          toCollect: 0,
          toServe: 0,
        };
        groups.set(key, group);
      }

      const next = nextAction(item.status);
      group.items.push({
        id: item.id,
        orderId: order.id,
        orderNumber: order.number,
        name: item.name,
        quantity: item.quantity,
        status: item.status,
        notes: item.notes,
        since: landedAt(item, order),
        next,
      });
      if (next === 'pickup') group.toCollect += 1;
      else group.toServe += 1;

      // One dine-in order anywhere in the trip makes the whole trip a table's,
      // whatever the others say — a merged visit may carry both.
      if (order.fulfillmentType === 'DINE_IN') group.takeaway = false;
      group.customerName ??= order.customerName;
      if (!group.orderNumbers.includes(order.number)) group.orderNumbers.push(order.number);
    }
  }

  const rows = [...groups.values()];
  for (const group of rows) {
    group.items.sort((a, b) => byAge(a.since, b.since) || compareNatural(a.name, b.name));
    // The trip's age is its oldest line, which after that sort is its first: a
    // table is as late as the coldest thing on it, not as late as its average.
    const oldest = group.items[0]?.since ?? null;
    group.waitingMs = oldest === null ? null : Math.max(0, now - oldest);
    group.orderNumbers.sort(compareNatural);
  }

  // Longest wait first, with the table number as tie-break so the order is
  // *total*: two tables called in the same second must not swap places between
  // renders, which on a live board looks like the screen twitching.
  return rows.sort(
    (a, b) =>
      byWait(a.waitingMs, b.waitingMs) ||
      compareNatural(a.tableNumber ?? '', b.tableNumber ?? '') ||
      compareNatural(a.key, b.key),
  );
}

/**
 * A wait in ms, split into what a label shows.
 *
 * Clamped at zero rather than going negative: a station screen whose clock runs
 * a minute behind the server would otherwise report food that goes ready in the
 * future, and "-1m" on the pass reads as a bug in the app rather than the clock.
 */
export function waitedFor(waitingMs: number | null): Waited | null {
  if (waitingMs === null || !Number.isFinite(waitingMs)) return null;
  const totalMinutes = Math.max(0, Math.floor(waitingMs / 60_000));
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60, totalMinutes };
}

/**
 * How many *orders* are on the pass.
 *
 * The unit is the order, not the plate: `ReadyAlertsService` writes one
 * `ORDER_READY` notification per order — the waiter's unit of work is the tray,
 * not the plate — so a header counted this way and the handheld's badge are
 * answering the same question and cannot contradict each other.
 */
export function passOrderCount(groups: readonly PassTrip[]): number {
  const orders = new Set<string>();
  for (const group of groups) for (const item of group.items) orders.add(item.orderId);
  return orders.size;
}

/** Every line in a trip owed a particular transition — one write each, no bulk route. */
export function itemsAwaiting(group: PassTrip, action: PassAction): PassItem[] {
  return group.items.filter((item) => item.next === action);
}
